<template>
  <h2>Introduction</h2>
  <div>
    <p>The data in the Energy Data Repository (EDR for short) is stored in a structured manner. This makes
      it easier to search and analyse the data in the EDR by both humans and machines. Besides the technology definition
      itself (which is
      defined in the ESDL language), the EDR introduces the Energy Data Definition (EDD for short). An EDD
      describes additional information (meta-data) about the technology (in ESDL), mainly aimed for human
      readability.<br/>
      It allows you to annotate the ESDL with some extra information, such as an image, a better title, publication
      date and validity year.
    </p>
  </div>

  <h2>The EnergyDataDefinition (EDD)</h2>

  <p>
    All data in the EDR is stored as an EDD object. It describes some metadata next to the ESDL data itself.
    Example information is:
  </p>
  <ul>
    <li>Author - the name of the person entering the data</li>
    <li>Description - a description of the object (not used at the moment yet)</li>
    <li>GraphURL - a link to a visualisation of a profile that shows a profile of timeseries data (will be removed in
      the near
      future
    </li>
    <li>PublicationDate - the date when the data was published</li>
    <li>Title - the title of the object in the EDR</li>
    <li>ValidityYear - the year for which the data is valid (can be used for filtering purposes)</li>
    <li>Image - an image to show next to the data</li>
    <li>Annotations - explanation of the data in the EDR, will not be part of the ESDL description itself</li>
  </ul>

  <h3>Annotations</h3>

  <p>
    Annotations are used to give the reader more context of the data. It can contain information about how data should
    be interpreted, how it was derived, where the data came from (reference to sources), and so on. The data is
    typically
    not used by models and therefor not part of the ESDL data itself. There are two types of annotations:
  </p>
  <ul>
    <li>
      CategoryAnnotation - An annotation of a "category" of data in the ESDL. The following categories are defined:
      <ul>
        <li>
          GENERAL - General information about the ESDL data. Will be shown as a first paragraph under the first table
          with basic attributes of the data
        </li>
        <li>TECHNICAL_DIMENSIONS - Information about the technical dimensions of the ESDL asset. Will be shown as a
          second paragraph under the first table
        </li>
        <li>
          COSTS - Cost information related to the ESDL asset. Will be shown under the table with cost information
        </li>
        <li>ENERGY_FLOWS - Information about energy flows of the asset in the ESDL data.</li>
        <li>MATERIAL_FLOWS - Information about material flows of the asset in the ESDL data.</li>
        <li>EMISSIONS - Information about emissions of the asset in the ESDL data</li>
        <li>OTHER - For other relevant information not fitting in the other categories</li>
      </ul>
    </li>
    <li>
      ObjectAnnotation - An annotation attached to an instance of an ESDL class in the tree of ESDL data. This
      information is not shown anywhere at the moment, but allows you to add extra information to a specific part
      of the ESDL, such as a specific attribute of a WindTurbine.
    </li>
  </ul>


  <h2>ESDL data</h2>

  <p>
    The core information in the EnergyDataDescription (EDD) is the ESDL data itself. Without information on the ESDL
    object itself, the EDD is useless. The following ESDL objects can be stored in the EDR:
  </p>
  <ul>
    <li>
      Energy assets - an ESDL object that describes an asset in the energy system. Examples are a wind turbine,
      a solar panel installation, a power plant.
    </li>
    <li>
      Other assets - assets that are not energy assets, like buildings or a
      compound assets (an asset composed of other assets)
    </li>
    <li>
      Profiles - profile (time series) data.
    </li>
    <li>
      Key figures - information about sectors, carriers, or quantities and units.
    </li>
  </ul>

  <h3>Typical ESDL asset description</h3>

  <p>
    The information that must be part of the ESDL description highly depends on the model that is used. Because of this,
    it's not possible to describe the requirements in detail here. People are advised to have a look in the
    documentation of the energy simulation or optimization model or talk to the energy model owners, before defining
    the data. However, two typical structures of ESDL based data are shown below.
  </p>


  <ul>
    <li>
      Asset: WindTurbine (<a
        href="https://edr.hesi.energy/cat/Assets/%2Fedr%2FPublic%2FAssets%2FNorth%20Sea%20Energy%2FWind%20turbines%2F15%20MW%20Wind%20turbine%20at%20sea.edd">EDR
      example</a>)
      <ul>
        <li>
          PowerCurveTable
          <ul>
            <li>Header [QuantityAndUnitType]: SPEED in m/s</li>
            <li>Header [QuantityAndUnitType]: POWER in kW</li>
            <li>Row [TableRow]</li>
            <li>Row [TableRow]</li>
            <li>...</li>
          </ul>
        </li>
        <li>
          CostInformation
          <ul>
            <li>
              InvestmentCosts [SingleValue]:
              <ul>
                <li>QuanityAndUnitType: COST in EUR/MW</li>
              </ul>
            </li>
            <li>
              FixedOperationAndMaintenanceCosts [SingleValue]
              <ul>
                <li>QuantityAndUnitType: COST in EUR/yr</li>
              </ul>
            </li>
          </ul>
        </li>
        <li>
          Port [OutPort]: Electricity
        </li>
        <li>
          DataSource
        </li>
      </ul>
    </li>
    <li>
      Asset: CHP (<a
        href="https://edr.hesi.energy/cat/Assets/%2Fedr%2FPublic%2FAssets%2FFactsheets-experimental%2F2020%2FGas-fired%20CHP%20plant%20-%20electricity%20production%20and%20district%20heating.edd">EDR
      example</a>)
      <ul>
        <li>Port [InPort]: Natural gas</li>
        <li>Port [OutPort]: Electricity</li>
        <li>Port [OurPort]: Heat</li>
        <li>
          InputOutputRelation (main port: Electricity)
          <ul>
            <li>PortRelation (Natural gas: 0.5)</li>
            <li>PortRelation (Heat: 1.25)</li>
          </ul>
        </li>
        <li>
          CostInformation
          <ul>
            <li>
              InvestmentCosts [SingleValue]:
              <ul>
                <li>QuanityAndUnitType: COST in EUR/MW</li>
              </ul>
            </li>
            <li>
              FixedOperationAndMaintenanceCosts [SingleValue]
              <ul>
                <li>QuantityAndUnitType: COST in EUR/yr</li>
              </ul>
            </li>
          </ul>
        </li>
        <li>
          DataSource
        </li>
      </ul>
    </li>
  </ul>


  <h2>Units in ESDL</h2>
  <p>
    Some units in ESDL are fixed and part of the language definition. In the EDD configurator you will find these
    units next to the edit boxes to enter the number. Examples are:
  </p>
  <ul>
    <li>The power attribute of an asset (WindTurbine) is always in Watt</li>
    <li>The volume attribute of a storage tank is always in m3</li>
  </ul>

  On other places the concept of "QuantityAndUnit" has been introduced. This provides a very flexible but explicit
  way to express both the quantity (energy, power, distance, ...) and the unit (Joule, Watt, meter, ...) of the value
  you specify. QuantityAndUnit is for example used with:

  <ul>
    <li>Profiles attached to ports</li>
    <li>CostInformation</li>
    <li>InputOutputRelation</li>
    <li>PowerCurveTable of a WindTurbine (and tables in general)</li>
  </ul>

</template>

<script setup>

</script>
